import {useMutation} from '@tanstack/react-query';
import {Dispatch, SetStateAction, useCallback, useState} from 'react';
import {storeMutationAdapter} from '../../../adapter/storeMutationAdapter';
import {getAgentCode} from '../../../common/getAgentCode.function';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {ApiStatusCodeVo} from '../../../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {commonErrorHandler} from '../../../errors/commonErrorHandler';
import {RedeemCodeType} from '../../../graphql/generated/types';
import {Dialog} from '../type';
import {getDialog} from '../util/getDialog';

export function useSubmitRedeem({
  redeemCode,
  setShowDialog,
  setDialog,
}: {
  redeemCode: string;
  setShowDialog: Dispatch<SetStateAction<boolean>>;
  setDialog: Dispatch<SetStateAction<Dialog | undefined>>;
}) {
  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = useState<any>();
  const {mutateAsync: UseRedeem} = useMutation(
    storeMutationAdapter.useRedeem()
  );

  const handleSubmitRedeemCode = useCallback(
    async ({isConfirmed}: {isConfirmed?: boolean}) => {
      if (!redeemCode) {
        setShowDialog(true);
        setDialog({
          title: t('more_coupon_register_invalid_redeem_popup'),
          secondButton: t('common_confirm_button'),
        });
        return;
      }

      setIsFetching(true);
      const data = await UseRedeem({
        input: {
          code: redeemCode.replace(/-/g, '').toUpperCase(),
          agentCode: getAgentCode(requestContext.requestInfo.display),
          confirmed: isConfirmed,
        },
      }).catch(error => {
        commonErrorHandler(error);
      });

      setIsFetching(false);

      const dialogMessage = getDialog({
        type: data?.useRedeem?.type ?? RedeemCodeType.None,
        title: data?.useRedeem?.title || undefined,
        ageGrade: data?.useRedeem?.age,
        resultCode: data?.useRedeem?.resultCode ?? ApiStatusCodeVo.UnknownError,
      });
      setData(data?.useRedeem);
      if (data?.useRedeem?.type === RedeemCodeType.Point) {
        if (data?.useRedeem?.shouldRedirect) {
          setShowDialog(true);
        } else {
          setShowDialog(false);
        }
      } else {
        setShowDialog(true);
      }
      setDialog(dialogMessage);
    },
    [UseRedeem, redeemCode, setDialog, setShowDialog]
  );

  return {
    isFetching,
    data,
    handleSubmitRedeemCode,
  };
}
